import type { Loan, UpdateLoanTermsInput } from '@taler/api-client'
import { loanKeys } from '@taler/api-client'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { problemDetail } from '../internal/problem.ts'

export interface UseLoanTermsOptions {
  readonly ledgerId: () => string | undefined
}

export interface UseLoanTerms {
  update(loanId: string, input: UpdateLoanTermsInput): Promise<Loan>
  readonly isSaving: ComputedRef<boolean>
  readonly hasError: ComputedRef<boolean>
  readonly errorDetail: ComputedRef<string | undefined>
}

export function useLoanTerms(options: UseLoanTermsOptions): UseLoanTerms {
  const api = useApi()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: ({ loanId, input }: { loanId: string; input: UpdateLoanTermsInput }) =>
      api.loans.updateTerms(options.ledgerId()!, loanId, input),
    onSuccess: () => {
      for (const key of loanKeys.invalidatedByWrite(options.ledgerId() ?? '')) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
    },
  })

  return {
    update: (loanId, input) => mutation.mutateAsync({ loanId, input }),
    isSaving: computed(() => mutation.isPending.value),
    hasError: computed(() => mutation.error.value !== null),
    errorDetail: computed(() => problemDetail(mutation.error.value)),
  }
}
